import { computed, ref } from 'vue';

/**
 * Anchors a floating control just above a bottom sheet while the sheet sits at or
 * below its intermediate snap: the control follows the sheet down to the collapsed
 * snap and back, and stays put above the intermediate height when the sheet opens
 * further — it must not drift up the screen ahead of a full-screen sheet.
 *
 * Wire `onHeight` to `update:height` and `onIntermediateHeight` to
 * `update:intermediateHeight`; `anchorHeight` is undefined until both are measured,
 * so the caller keeps its own floor in the meantime.
 */
export type BottomSheetAnchor = ReturnType<typeof useBottomSheetAnchor>;

export function useBottomSheetAnchor() {
  const height = ref<number>(0);
  const intermediateHeight = ref<number>(0);
  const dragging = ref<boolean>(false);

  const anchorHeight = computed<number | undefined>(() => {
    if (!height.value || !intermediateHeight.value) return undefined;
    return Math.min(height.value, intermediateHeight.value);
  });

  function onHeight(value: number, isDragging: boolean) {
    height.value = value;
    dragging.value = isDragging;
  }

  function onIntermediateHeight(value: number) {
    intermediateHeight.value = value;
  }

  return { anchorHeight, dragging, onHeight, onIntermediateHeight };
}
